import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { useApiRequest } from "@hooks/use-api-request";
import { HttpMethod } from "@lib/http.helpers";

import { WORK_HOURS_PER_YEAR } from "src/constants";

const divideAnnualIncomeBy = (divisor) => (value) => {
  return divisor > 0
    ? { ...value, earnings: value.earnings / divisor }
    : { ...value };
};

export const useCareerDetails = ({
  transfrId,
  index,
  careerMatch = {},
  regionAltCode,
  keepPreviousData = false,
}) => {
  const queryClient = useQueryClient();

  const getMinimalData = (previousData) => {
    if (keepPreviousData) {
      return previousData;
    }

    const minimalData =
      careerMatch ??
      queryClient
        .getQueryData(["user-favorite-careers"])
        ?.map(({ career }) => career)
        ?.find((c) => c.transfrId == transfrId) ??
      {};

    return {
      cluster: { name: "" },
      regional: undefined,
      national: undefined,
      ...minimalData,
    };
  };

  const minimalData = useMemo(() => getMinimalData(), [transfrId]);

  const queryFn = useApiRequest({
    url: `/careers/${transfrId}`,
    method: HttpMethod.Get,
    headers: {
      "trk-evt-meta": btoa(JSON.stringify({ list_rank: index })),
    },
    params: {
      region_alt_code: regionAltCode,
    },
  });

  const query = useQuery({
    queryKey: ["career-details", transfrId, regionAltCode],
    queryFn: queryFn,
    placeholderData: getMinimalData,
    select: (data) => {
      // Calculate monthly and hourly earnings using the annual earning values
      let national;
      let regional;

      if (data.regional) {
        regional = {
          ...data.regional,
          earnings: {
            annual: data.regional.earnings,
            monthly: data.regional.earnings.map(divideAnnualIncomeBy(12)),
            hourly: data.regional.earnings.map(
              divideAnnualIncomeBy(WORK_HOURS_PER_YEAR),
            ),
          },
        };
      }

      if (data.national) {
        national = {
          ...data.national,
          earnings: {
            annual: data.national.earnings,
            monthly: data.national.earnings.map(divideAnnualIncomeBy(12)),
            hourly: data.national.earnings.map(
              divideAnnualIncomeBy(WORK_HOURS_PER_YEAR),
            ),
          },
        };
      }

      return { ...data, national, regional };
    },
  });

  return {
    ...query,
    minimalData,
  };
};
